/**
 * JWT auth helper — reads the same `authentik_secure_tokens` from localStorage
 * that the Vue-side apiClient uses, and returns headers suitable for fetch().
 */

import { getDeveloperVersion, shouldSendDeveloperVersion } from "../../utils/developerVersion";
import { getApiBaseUrl } from "./api-url";

export function getAuthToken(): string | null {
  try {
    const stored = localStorage.getItem("authentik_secure_tokens");
    if (!stored) return null;

    const tokenData = JSON.parse(stored);
    if (Date.now() >= tokenData.expiresAt) return null;

    const tokens = JSON.parse(atob(tokenData.tokens));
    return tokens.access_token || tokens.id_token;
  } catch {
    return null;
  }
}

/**
 * Returns headers for an outbound fetch().
 *
 * Pass `requestUrl` whenever the caller builds the URL from a base other than
 * `getApiBaseUrl()` (e.g. atlas loaders use `platformDataBase()`). When omitted,
 * the developer_version gate falls back to `getApiBaseUrl()`, which already
 * honors MountOptions / VUE_APP_AI_API_URL / NEXT_PUBLIC_AI_API_URL overrides.
 * This keeps the staging-only routing header from leaking onto a production
 * override host.
 */
export function authHeaders(requestUrl?: string | null): Record<string, string> {
  const token = getAuthToken();
  const developerVersion = getDeveloperVersion();
  const targetUrl = requestUrl ?? getApiBaseUrl();
  const headers: Record<string, string> = { version: "2021-04-15", "source-id": "123" };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (developerVersion && shouldSendDeveloperVersion(targetUrl)) {
    headers.developer_version = developerVersion;
  }
  return headers;
}

export interface AuthUser {
  email: string | null;
  name: string | null;
}

/**
 * Decode the JWT payload to extract user info (email, name).
 * Does NOT validate the signature — that's the backend's job.
 */
export function getAuthUser(): AuthUser {
  const token = getAuthToken();
  if (!token) return { email: null, name: null };

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return {
      email: payload.email ?? payload.preferred_username ?? null,
      name: payload.name ?? null,
    };
  } catch {
    return { email: null, name: null };
  }
}

/**
 * Format a user identity string from name and email.
 * Shows "Name (email)" when both exist, otherwise whichever is available.
 */
export function formatUserIdentity(name?: string | null, email?: string | null): string | null {
  if (name && email) return `${name} (${email})`;
  return name || email || null;
}
